import { useMemo, type ReactNode } from 'react';
import { graphql, useFragment, useLazyLoadQuery } from 'react-relay';

import { Payment } from '@woovi/components';
import { usePooling } from '@woovi/hooks';
import { useMutationCallbacks, withRelayBoundary } from '@woovi/relay';
import { CHARGE_STATUS_ENUM } from '@woovi/shared';

import type { WooviPayPlugin_charge$key } from './__generated__/WooviPayPlugin_charge.graphql.ts';
import type { WooviPayPlugin_query$key } from './__generated__/WooviPayPlugin_query.graphql.ts';
import type { WooviPayPluginQuery } from './__generated__/WooviPayPluginQuery.graphql.ts';
import type { ChargeUpdateStatusMutation } from '../plugin/__generated__/ChargeUpdateStatusMutation.graphql.ts';
import { ChargeUpdateStatus } from '../plugin/ChargeUpdateStatusMutation.tsx';
import { useChargeUpdateStatusSubscription } from '../subscription/useChargeUpdatedStatus.ts';

type WooviPayPluginProps = {
  correlationID: string | null;
  parentFetchKey: number;
  withCard?: boolean;
};

const WOOVI_PAY_POLLING_INTERVAL = 10000; // 10 seconds

const WooviPayPlugin = ({
  correlationID,
  withCard = true,
  parentFetchKey,
}: WooviPayPluginProps): ReactNode => {
  const variables = useMemo(
    () => ({
      correlationID,
    }),
    [correlationID],
  );

  const response = useLazyLoadQuery<WooviPayPluginQuery>(
    graphql`
      query WooviPayPluginQuery($correlationID: String) {
        ...WooviPayPlugin_query
        charge(correlationID: $correlationID) {
          ...WooviPayPlugin_charge
        }
      }
    `,
    variables,
    {
      fetchPolicy: 'store-and-network',
      fetchKey: parentFetchKey,
    },
  );

  const query = useFragment<WooviPayPlugin_query$key>(
    graphql`
      fragment WooviPayPlugin_query on Query {
        ...Payment_query
      }
    `,
    response,
  );

  const charge = useFragment<WooviPayPlugin_charge$key>(
    graphql`
      fragment WooviPayPlugin_charge on Charge {
        id
        status
        paymentMethods {
          ... on PaymentMethodPix {
            method
          }
        }
        ...Payment_charge
      }
    `,
    response.charge,
  );

  const getShouldPoll = (): boolean => {
    if (!charge) {
      return false;
    }

    if (!charge.paymentMethods?.length) {
      return false;
    }

    const isChargeCompleted = charge?.status === CHARGE_STATUS_ENUM.COMPLETED;
    const isChargeExpired = charge?.status === CHARGE_STATUS_ENUM.EXPIRED;

    if (isChargeCompleted) {
      return false;
    }

    if (isChargeExpired) {
      return false;
    }

    return true;
  };

  const shouldPoll = getShouldPoll();

  const [chargeUpdateStatus] = useMutationCallbacks<ChargeUpdateStatusMutation>(
    {
      name: 'ChargeUpdateStatus',
      mutation: ChargeUpdateStatus,
      shouldShowSnackbar: false,
    },
  );

  const fetchChargeStatus = (): void => {
    const input = {
      id: charge?.id as string,
    };

    const config = {
      variables: {
        input,
      },
    };

    chargeUpdateStatus(config);
  };

  useChargeUpdateStatusSubscription({
    input: {
      chargeId: charge?.id,
    },
  });

  usePooling(shouldPoll, fetchChargeStatus, WOOVI_PAY_POLLING_INTERVAL);

  if (!query || !charge) {
    return null;
  }

  return (
    <Payment
      query={query}
      charge={charge}
      withCard={withCard}
      paymentLink={null}
      pixQrCode={null}
      shouldRedirect
    />
  );
};

export default withRelayBoundary(WooviPayPlugin, {
  loadingView: null,
});
